import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { describeError } from '../../ui/problemDetails.ts';
import { canInvite, type Group } from '../groups/groupsApi.ts';
import {
  createInvitation,
  getGroupInvitations,
  invitationKeys,
  revokeInvitation,
} from './invitationsApi.ts';

/**
 * Invite someone, and see who has been invited but not yet joined.
 *
 * The whole body of its own dialog, opened from the menu — the address field is the first
 * thing in it rather than the fourth section of Settings. The menu already hides the entry
 * from people who may not invite; the same check is repeated here so that no future caller
 * can render a form that would only ever fail.
 */
export function InviteMemberForm({ group }: { group: Group }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');

  const mayInvite = canInvite(group);

  const { data: outstanding } = useQuery({
    queryKey: invitationKeys.ofGroup(group.id),
    queryFn: () => getGroupInvitations(group.id),
    enabled: mayInvite,
  });

  const invite = useMutation({
    mutationFn: (address: string) => createInvitation(group.id, address),
    onSuccess: async () => {
      setEmail('');
      await queryClient.invalidateQueries({ queryKey: invitationKeys.ofGroup(group.id) });
    },
  });

  const revoke = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(group.id, invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: invitationKeys.ofGroup(group.id) });
    },
  });

  if (!mayInvite) {
    return null;
  }

  const trimmed = email.trim();

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (trimmed.length > 0) {
      invite.mutate(trimmed);
    }
  }

  return (
    <div className="settings">
      {/* No heading of its own: the dialog is titled "Invite someone", and repeating it
          here would put two words above one input. */}
      <section className="settings-section">
        <form className="settings-row" onSubmit={handleSubmit}>
          <input
            className="settings-row__input"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="their@email.com"
            aria-label="Email to invite"
            autoComplete="off"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
          />
          <button
            className="button button--primary button--small"
            type="submit"
            disabled={invite.isPending || trimmed.length === 0}
          >
            Send
          </button>
        </form>

        {invite.isError && (
          <p className="form-error" role="alert">
            {describeError(invite.error)}
          </p>
        )}

        {invite.isSuccess && !invite.isPending && (
          <p className="settings-status">Invitation sent.</p>
        )}

        <p className="settings-hint">
          They will get an email with a link. Nothing happens to their account until they
          accept it.
        </p>
      </section>

      {/* Its own section, so the hairline separates the thing you came to do from the
          record of the last few times you did it. */}
      {outstanding && outstanding.length > 0 && (
        <section className="settings-section">
          <h3 className="settings-section__title">Invited, not joined yet</h3>

          <ul className="settings-people">
            {outstanding.map((invitation) => (
              <li key={invitation.id} className="settings-person">
                <span className="muted">{invitation.invitedEmail}</span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => revoke.mutate(invitation.id)}
                  disabled={revoke.isPending}
                >
                  Withdraw
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
